<template>
  <div>
    <AlertLayout
      title="Reminders!"
      message="Incorrect screening results could be fatal to recipients. Kindly ensure you enter the correct results and information below.
      Kindly note that you can only process one unit at a time"
      type="warning"
    />

    <div class="flex gap-6 px-10 py-2 border border-t-0">
      <!-- Left Column - Form Inputs -->
      <div class="w-1/2 space-y-4">
        <h2 class="text-lg font-bold mb-6">Issue Out Product</h2>

        <div>
          <label class="block mb-2 text-gray-700 font-medium"
            >Issue to (Facility Name)</label
          >
          <input
            type="text"
            v-model="formData.facility"
            class="w-full p-2.5 border rounded placeholder-gray-400 shadow-sm shadow-gray-300"
            placeholder="Enter facility name"
            required
          />
        </div>

        <div>
          <label class="block mb-2 text-gray-700 font-medium"
            >Select blood group</label
          >
          <div class="relative">
            <select
              v-model="formData.blood_group_id"
              class="w-full p-2.5 border rounded placeholder-gray-400 shadow-sm shadow-gray-300 appearance-none"
              @change="filterUnitProducts"
              required
            >
              <option value="" disabled>Select blood group</option>
              <option
                v-for="group in bloodGroups"
                :key="group.blood_group_id"
                :value="group.blood_group_id"
              >
                {{ group.description }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div>
          <label class="block mb-2 text-gray-700 font-medium"
            >Pint Serial</label
          >
          <div class="relative">
            <div
              class="w-full p-2.5 border rounded placeholder-gray-400 shadow-sm shadow-gray-300 cursor-pointer"
              @click="toggleDropdown"
            >
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(productId, index) in formData.product_ids"
                  :key="productId"
                  class="bg-blue-100 text-blue-600 px-2 py-1 rounded"
                >
                  {{ getProductName(productId) }}
                  <span
                    class="cursor-pointer ml-1 text-red-500"
                    @click.stop="removeProduct(index)"
                    >×</span
                  >
                </span>
              </div>
              <div v-if="!formData.product_ids.length" class="text-gray-400">
                Select unit serial number
              </div>
            </div>
            <div
              v-if="isDropdownVisible"
              class="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg"
            >
              <ul class="max-h-60 overflow-y-auto">
                <li
                  v-for="product in filteredUnitProducts"
                  :key="product.product_id"
                  class="px-4 py-2 cursor-pointer hover:bg-gray-100"
                  @click="selectProduct(product.product_id)"
                >
                  {{ product.pint_serial }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Issued By and Date - Side by Side -->
        <div class="flex gap-4">
          <div class="w-1/2">
            <label class="block mb-2 text-gray-700 font-medium"
              >Issued By</label
            >
            <select
              v-model="formData.user_id"
              class="w-full p-2.5 border rounded placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            >
              <option value="" disabled>Select User</option>
              <option
                v-for="user in users"
                :key="user.user_id"
                :value="user.user_id"
              >
                {{ user.first_name }} {{ user.last_name }}
              </option>
            </select>
          </div>
          <div class="w-1/2">
            <label class="block mb-2 text-gray-700 font-medium"
              >Issue Date</label
            >
            <input
              type="datetime-local"
              v-model="formData.issued_date"
              class="w-full p-2.5 border rounded placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            />
          </div>
        </div>

        <!-- Received By and Date - Side by Side -->
        <div class="flex gap-4">
          <div class="w-1/2">
            <label class="block mb-2 text-gray-700 font-medium"
              >Received by</label
            >
            <input
              type="text"
              v-model="formData.received_by"
              class="w-full p-2.5 border rounded placeholder-gray-400 shadow-sm shadow-gray-300"
              placeholder="Enter name"
              required
            />
          </div>
          <div class="w-1/2">
            <label class="block mb-2 text-gray-700 font-medium"
              >Received Date</label
            >
            <input
              type="datetime-local"
              v-model="formData.received_date"
              class="w-full p-2.5 border rounded placeholder-gray-400 shadow-sm shadow-gray-300"
              required
            />
          </div>
        </div>

        <div class="flex items-center justify-between mt-8">
          <button
            @click="handleBackToInventory"
            class="px-4 py-2 border rounded text-orange-500 border-orange-500"
          >
            Back
          </button>
          <div class="flex items-center gap-4">
            <button
              @click="resetForm"
              class="px-4 py-2 border rounded text-orange-500 border-orange-500"
            >
              Cancel
            </button>
            <button
              @click="handleIssue"
              class="px-8 py-2 bg-yellow-500 text-white rounded"
            >
              Issue Out
            </button>
            <button @click="handleIssueAndDownload" class="px-8 py-2 bg-yellow-500 text-white rounded">
              Issue Out & Download PDF
            </button>
          </div>
        </div>
      </div>

      <!-- Right Column - Table -->
      <div class="w-1/2">
        <table class="w-full text-left text-gray-700">
          <thead>
            <tr class="border-b">
              <th class="px-6 py-3">Pint Serial</th>
              <th class="px-6 py-3">Product</th>
              <th class="px-6 py-3">Volume</th>
              <th class="px-6 py-3">Blood Group</th>
              <th class="px-6 py-3">Expiry Date</th>
              <th class="px-6 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(product, index) in selectedProducts"
              :key="product.product_id"
              class="border-b"
            >
              <td class="px-6 py-3">{{ product.pint_serial }}</td>
              <td class="px-6 py-3">{{ product.unit_product.name }}</td>
              <td class="px-6 py-3">{{ product.volume }}</td>
              <td class="px-6 py-3">{{ product.blood_group.name }}</td>
              <td class="px-6 py-3">
                {{ new Date(product.expiry_date).toLocaleDateString("en-CA") }}
              </td>
              <td class="px-6 py-3">
                <button
                  type="button"
                  class="p-1 ml-3 bg-red-500 rounded"
                  @click="removeProduct(index)"
                >
                  <svg
                    class="w-4 h-4 text-white"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import AlertLayout from "@/components/AlertLayout.vue";
import api from "@/services/api";
import SweetAlertService from "@/services/SweetAlertService";

export default {
  name: "ExternalUnitIssuanceForm",
  components: {
    AlertLayout,
  },
  data() {
    return {
      user: "",
      bloodGroups: [],
      bloodProducts: [],
      unitProducts: [],
      users: [],
      filteredUnitProducts: [],
      selectedProducts: [],
      isDropdownVisible: false,
      formData: {
        facility: "",
        user_id: "",
        received_by: "",
        received_date: "",
        issued_date: "",
        product_ids: [],
        unit_count: "",
      },
      status: "active",
      error: null,
      facility_logo: "",
      facility_details: "",
    };
  },
  mounted() {
    this.user = this.$store.getters["auth/user"];
    this.facility_logo = this.user.user_roles[0]?.facility_department?.facility?.logo;
    this.facility_details = this.user.user_roles[0]?.facility_department?.facility;
    this.fetchBloodGroups();
    this.fetchBloodProducts();
    this.fetchUnitProducts();
    this.getUsers();
  },
  methods: {
    handleBackToInventory() {
      this.$router.push({ name: "InventoryManagementPage" });
    },

    validateForm() {
      const requiredFields = [
        "blood_group_id",
        "facility",
        "user_id",
        "received_by",
        "received_date",
        "issued_date",
        "product_ids",
        "unit_count",
      ];

      const emptyFields = requiredFields.filter(
        (field) => !this.formData[field]
      );
      return emptyFields.map((field) => field.replace(/_/g, " "));
    },

    async handleSubmitNewUnit() {
      const emptyFields = this.validateForm();
      if (emptyFields.length > 0) {
        SweetAlertService.showEmptyFieldsAlert(emptyFields);
        return;
      }

      try {
        await api.createUnitTransfer(this.formData);
        await SweetAlertService.showSuccess(
          "Success!",
          "Unit transferred successfully!"
        );
        this.$router.push({ name: "InventoryManagementPage" });
      } catch (error) {
        this.error = error.message || "Failed to save unit transfer";
        console.error("Error:", this.error);
        SweetAlertService.showError("Error", "Error updating unit transfer.");
      }
    },

    async fetchBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response;
      } catch (error) {
        console.error("Error fetching blood groups:", error);
      }
    },

    async getUsers() {
      try {
        const response = await api.getUsers();
        this.users = response.filter(
          (user) => Array.isArray(user.user_roles) && user.user_roles.length > 0
        );
      } catch (error) {
        this.error = error.message || "Failed to fetch users";
      }
    },

    async fetchBloodProducts() {
      try {
        const response = await api.getUnitProducts();
        this.bloodProducts = response || [];
      } catch (error) {
        this.error = "Failed to fetch blood products";
      }
    },

    async fetchUnitProducts() {
      try {
        const response = await api.fetchAllProducts();
        this.unitProducts = (response || []).filter(
          (product) => product.status === "Okay"
        );
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    },

    filterUnitProducts() {
      if (this.formData.blood_group_id) {
        this.filteredUnitProducts = this.unitProducts.filter(
          (product) => product.blood_group_id === this.formData.blood_group_id
        );
      } else {
        this.filteredUnitProducts = [];
      }
    },

    resetForm() {
      this.formData = {
        blood_group_id: "",
        facility: "",
        user_id: "",
        received_by: "",
        received_date: "",
        issued_date: "",
        product_ids: [],
        unit_count: "",
      };
      this.selectedProducts = [];
    },

    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },

    getProductName(productId) {
      const product = this.filteredUnitProducts.find(
        (p) => p.product_id === productId
      );
      return product ? product.pint_serial : "";
    },

    selectProduct(productId) {
      const selectedProduct = this.filteredUnitProducts.find(
        (product) => product.product_id === productId
      );

      if (
        selectedProduct &&
        !this.selectedProducts.some(
          (product) => product.product_id === productId
        )
      ) {
        this.selectedProducts.push(selectedProduct);
        this.formData.unit_count = this.selectedProducts.length;

        this.formData.product_ids = this.selectedProducts.map(
          (product) => product.product_id
        );
      }
      this.isDropdownVisible = false;
    },

    removeProduct(index) {
      this.selectedProducts.splice(index, 1);
      this.formData.unit_count = this.selectedProducts.length;

      this.formData.product_ids = this.selectedProducts.map(
        (product) => product.product_id
      );
    },

    async handleIssue() {
      const emptyFields = this.validateForm();
      if (emptyFields.length > 0) {
        SweetAlertService.showEmptyFieldsAlert(emptyFields);
        return;
      }

      try {
        await api.createUnitTransfer(this.formData);
        await SweetAlertService.showSuccess('Success!', 'Unit transferred successfully!');
        this.$router.push({ name: 'InventoryManagementPage' });
      } catch (error) {
        this.error = error.message || 'Failed to save unit transfer';
        console.error('Error:', this.error);
        SweetAlertService.showError('Error', 'Error updating unit transfer.')
      }
    },

    async handleIssueAndDownload() {
      const emptyFields = this.validateForm();
      if (emptyFields.length > 0) {
        SweetAlertService.showEmptyFieldsAlert(emptyFields);
        return;
      }

      try {
        const response = await api.createUnitTransfer(this.formData);
        this.generatePDF(response.unit_transfer.unit_transfer_id);
        await SweetAlertService.showSuccess(
          "Success!",
          "Unit transferred successfully and PDF downloaded!"
        );
        this.$router.push({ name: "InventoryManagementPage" });
      } catch (error) {
        this.error = error.message || "Failed to save unit transfer";
        console.error("Error:", this.error);
        SweetAlertService.showError("Error", "Error updating unit transfer.");
      }
    },

    async convertImageToBase64(url) {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    },

    async convertSvgToPngBase64(svgUrl) {
      const response = await fetch(svgUrl);
      const svgText = await response.text();
      return new Promise((resolve, reject) => {
        const img = new Image();
        const blob = new Blob([svgText], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
      });
    },

    async generatePDF(unit_transfer_id) {
      const doc = new jsPDF();

      // Fetch unit transfer data
      const response = await api.getUnitTransfer(unit_transfer_id);
      const unit_transfers = response.data || response;
      const transfers_products = unit_transfers.unit_transfer_products;

      // Load logo
      let image;
      if (this.facility_logo.endsWith(".svg")) {
        image = await this.convertSvgToPngBase64(this.facility_logo);
      } else {
        image = await this.convertImageToBase64(this.facility_logo);
      }

      if (!image) {
        console.error("Image conversion failed!");
        return;
      }

      // Document setup
      const pageWidth = doc.internal.pageSize.width;

      doc.addImage(image, "PNG", 15, 10, 30, 20);

      // Add facility contact info below the logo, centered
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`${this.facility_details.name}`, pageWidth - 15, 15, { align: "right" });
      doc.text(`${this.facility_details.third_level_devision} Kenya`, pageWidth - 15, 20, { align: "right" });
      doc.text(`${this.facility_details.email}`, pageWidth - 15, 25, { align: "right" });

      // Add date field with the actual date
      doc.setFontSize(11);
      const issueDate = new Date(unit_transfers.issued_date).toLocaleDateString();
      doc.text(`DATE: ${issueDate}`, 15, 50);

      // Add reference header with underline
      doc.setFont("helvetica", "bold");
      doc.text("REF: REQUISITION AND ISSUANCE FORM OF BLOOD AND BLOOD PRODUCTS FROM/TO", 15, 65);
      doc.text("OTHER HEALTH INSTITUTIONS.", 45, 72);
      doc.setFont("helvetica", "normal");

      // Add requisition and issuance fields with values
      doc.text("REQUISITION FROM: _____________________________", 15, 85);
      doc.text(`ISSUANCE TO: ${unit_transfers.facility}`, 15, 95);

      // Add detailed units table
      const tableColumns = [
        { header: "#", dataKey: "index" },
        { header: "Serial No.", dataKey: "serial" },
        { header: "Group", dataKey: "group" },
        { header: "Product", dataKey: "product" },
        { header: "Volume", dataKey: "volume" },
        { header: "Days to Expiry", dataKey: "expiry" },
      ];

      const tableData = transfers_products.map((product, index) => ({
        index: index + 1,
        serial: product.product.pint_serial,
        group: product.product.blood_group.name,
        product: product.product.unit_product.name,
        volume: product.product.volume,
        expiry: this.calculateDaysToExpiry(product.product.expiry_date),
      }));

      doc.autoTable({
        startY: 105,
        columns: tableColumns,
        body: tableData,
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3, lineWidth: 0.5 },
        headStyles: {
          fillColor: [200, 200, 200],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          lineWidth: 0.5,
        },
        columnStyles: {
          index: { cellWidth: 15 },
          serial: { cellWidth: 40 },
          group: { cellWidth: 30 },
          product: { cellWidth: 30 },
          volume: { cellWidth: 30 },
          expiry: { cellWidth: 35 },
        },
      });

      // Add hemovigilance disclaimer in a light gray box
      const finalY = doc.previousAutoTable.finalY + 15;
      doc.setFillColor(240, 240, 240);
      doc.rect(15, finalY, pageWidth - 30, 40, "F");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text("For hemovigilance report communicate any incidences events.", 17, finalY + 10);
      doc.text(`${this.facility_details.name} does not take any liability that may arise out of use of the blood / blood`, 17, finalY + 20);
      doc.text("products.", 17, finalY + 25);
      doc.text("It's the responsibility of the health institutions carrying out transfusion of the blood", 17, finalY + 35);
      doc.text("products to ensure the safety of the blood/blood products before transfusion.", 17, finalY + 40);

      // Add signature fields with values
      const signatureY = finalY + 55;
      doc.setFontSize(10);
      doc.text("Requested by: ______________________________", 15, signatureY);
      doc.text("Sign: ____________", 100, signatureY);
      doc.text("Date: ________________", 140, signatureY);

      doc.text(`Issued by: ${unit_transfers.user.first_name} ${unit_transfers.user.last_name}`, 15, signatureY + 15);
      doc.text("Sign: ____________", 100, signatureY + 15);
      doc.text(`Date: ${new Date(unit_transfers.issued_date).toLocaleString()}`, 140, signatureY + 15);

      doc.text(`Received by: ${unit_transfers.received_by || "N/A"}`, 15, signatureY + 30);
      doc.text("Sign: ____________", 100, signatureY + 30);
      doc.text(`Date: ${new Date(unit_transfers.received_date).toLocaleString()}`, 140, signatureY + 30);

      // Add footer note
      doc.text(`On behalf of ${this.facility_details.name}.`, 15, signatureY + 50);


      // Save the PDF
      doc.save("blood-issuance-form.pdf");
    },

    calculateDaysToExpiry(expiryDate) {
      const expiry = new Date(expiryDate);
      const today = new Date();
      const diffTime = Math.abs(expiry - today);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} days`;
    },
  },
};
</script>